import axios, { type AxiosResponse } from 'axios'
import BaseService from '../BaseService'

import type { ChatContext } from '@/components/chatbot/chatContext.types'

// ==================== Type Definitions ====================

/** Message author types */
export type MessageAuthor = 'USER' | 'ASSISTANT'

/** Feedback status types */
export type ChatFeedbackStatus = 'NEUTRAL' | 'POSITIVE' | 'NEGATIVE'

/** Chat message */
export interface ChatMessage {
  author: MessageAuthor
  text: string
}

/** Chat feedback */
export interface ChatFeedback {
  status: ChatFeedbackStatus
}

/** Response from sending a message — a single bot reply, not the full conversation */
export interface ChatResponse {
  id: string
  text: string
  created_at: string
}

/** Full conversation history, as returned by GET /chat/{id} (not currently used by this service) */
export interface ChatHistoryResponse {
  id: string
  messages: ChatMessage[]
  created_at: string
  feedback: ChatFeedback | null
}

/** Request to send a message to chatbot */
export interface PutMessageRequest {
  text: string
  id?: string
  context?: ChatContext
}

/** Request to send feedback */
export interface SendFeedbackRequest {
  status: ChatFeedbackStatus
  id: string
}

// ==================== Service Class ====================

class ChatbotService extends BaseService {
  constructor() {
    super({
      baseUrl: import.meta.env.VITE_PREMIUM_API_URL,
    })
  }

  /**
   * Send message to chatbot. Unauthenticated by design — also used from marketing pages.
   */
  putMessage({ text, id, context }: PutMessageRequest): Promise<AxiosResponse<ChatResponse>> | undefined {
    if (!text) return undefined

    return axios({
      url: `${this.baseUrl}/chat`,
      method: 'PUT',
      data: {
        text,
        ...(id && { id }),
        init_page_url: window.location.href,
        context,
      },
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        ...this.getTrafficAttributionHeaders(),
      },
    })
  }

  /**
   * Send feedback for chatbot response. Unauthenticated by design — also used from marketing pages.
   */
  sendFeedback({ status, id }: SendFeedbackRequest): Promise<AxiosResponse<void>> | undefined {
    if (!id || !status) return undefined

    return axios({
      url: `${this.baseUrl}/chat/${id}/feedback`,
      method: 'PUT',
      data: {
        status,
      },
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        ...this.getTrafficAttributionHeaders(),
      },
    })
  }
}

export default new ChatbotService()
